<template>
  <div class="image-upload">
    <input ref="fileInput" class="hidden-input" type="file" accept="image/png,image/jpeg,image/jpg" @change="onFileChange">
    <div
      class="upload-box"
      :class="{ 'has-image': !!modelValue }"
      @click="onBoxClick"
      @contextmenu.prevent="pick"
    >
      <template v-if="modelValue">
        <img class="upload-preview-img" :src="previewUrl" alt="preview">
        <div class="upload-overlay">
          <button type="button" class="upload-act" title="预览" @click.stop="preview">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
          <button type="button" class="upload-act" title="删除" @click.stop="remove">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </button>
        </div>
      </template>
      <template v-else>
        <div class="upload-plus">+</div>
        <div class="upload-text">{{ placeholder }}</div>
      </template>
    </div>
    <small v-if="hint" class="upload-hint">{{ hint }}</small>
    <small v-if="statusMsg" class="upload-status">{{ statusMsg }}</small>

    <div v-if="previewOpen" class="preview-mask" @click="previewOpen = false">
      <img class="preview-full" :src="previewUrl" alt="preview" @click.stop>
      <button type="button" class="preview-close" aria-label="关闭预览" @click="previewOpen = false">×</button>
    </div>
  </div>
</template>

<script>
import { BASE_URL, uploadImage } from '@/api'

export default {
  name: 'ImageUploadField',
  props: {
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: '选择图片' },
    hint: { type: String, default: '请上传大小不超过 10MB，格式为 .png / .jpg / .jpeg 的图片' },
    disabled: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'uploaded', 'error'],
  data() {
    return {
      statusMsg: '',
      pickBusy: false,
      previewOpen: false,
    }
  },
  computed: {
    previewUrl() {
      const url = String(this.modelValue || '').trim()
      if (!url) return ''
      if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url
      const base = String(BASE_URL || '').replace(/\/$/, '')
      if (base) return `${base}${url.startsWith('/') ? url : `/${url}`}`
      if (typeof window !== 'undefined' && window.location?.origin) {
        return `${window.location.origin}${url.startsWith('/') ? url : `/${url}`}`
      }
      return url
    },
  },
  methods: {
    onBoxClick() {
      if (!this.modelValue) this.pick()
    },
    pick() {
      if (this.disabled || this.pickBusy) return
      this.pickBusy = true
      this.$refs.fileInput?.click()
      window.setTimeout(() => { this.pickBusy = false }, 300)
    },
    preview() {
      if (!this.previewUrl) return
      this.previewOpen = true
    },
    remove() {
      if (this.disabled) return
      this.$emit('update:modelValue', '')
      this.statusMsg = ''
    },
    async onFileChange(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      const maxBytes = 10 * 1024 * 1024
      if (file.size > maxBytes) {
        this.statusMsg = ''
        this.$emit('error', new Error('图片不能超过 10MB'))
        return
      }
      try {
        this.statusMsg = '上传中...'
        const data = await uploadImage(file)
        const url = data?.url || ''
        this.$emit('update:modelValue', url)
        this.$emit('uploaded', url)
        this.statusMsg = '上传成功'
      } catch (err) {
        this.statusMsg = ''
        this.$emit('error', err)
      }
    },
  },
}
</script>

<style scoped>
.hidden-input { display: none; }
.upload-box {
  position: relative;
  width: 148px;
  height: 148px;
  border: 1px dashed #d0d7e2;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #fafbfc;
  overflow: hidden;
}
.upload-box:hover { border-color: #9bb7ff; background: #f5f8ff; }
.upload-box.has-image { cursor: default; border-style: solid; }
.upload-box.has-image:hover { border-color: #d0d7e2; background: #fafbfc; }
.upload-plus { font-size: 28px; color: #98a2b3; line-height: 1; }
.upload-text { font-size: 12px; color: #98a2b3; margin-top: 4px; }
.upload-preview-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.upload-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: rgba(0, 0, 0, 0.45);
  opacity: 0;
  transition: opacity .15s ease;
}
.upload-box.has-image:hover .upload-overlay { opacity: 1; }
.upload-act {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.upload-act:hover { background: rgba(255, 255, 255, 0.15); }
.upload-hint { display: block; margin-top: 8px; color: #98a2b3; font-size: 12px; line-height: 1.5; }
.upload-status { display: block; margin-top: 4px; color: #067647; font-size: 12px; }
.preview-mask {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}
.preview-full {
  max-width: min(92vw, 960px);
  max-height: 88vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.35);
}
.preview-close {
  position: fixed;
  top: 20px;
  right: 24px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
}
.preview-close:hover { background: rgba(255, 255, 255, 0.22); }
</style>
